import React from 'react'
import { WarningOutlined } from '@ant-design/icons'
import {
  CustomReportSectionsType,
  CustomReportTableSection
} from '../../../../../redux/context/customReports/typesSection'
import { getTitleByLanguage } from '../customReportTable/utils'
import SectionContent from '../section/SectionContent'
import PrintTableSection from './PrintTableSection'
import PrintTableSectionTransposed from './PrintTableSectionTransposed'
import { ErrorPlaceholder, SectionTitle, CardFrame } from './StyledPaper'

interface PrintSectionProps {
  section: CustomReportSectionsType | null
  sectionName?: string
  fixedHeight?: number
  sectionWidthPx?: number
  printableWidthPx: number
}

const HEADER_HEIGHT = 32

const GROWABLE_TYPES = new Set(['table', 'text'])

const cardHeight = (fixed: number | undefined, showTitle: boolean): string | number => {
  if (fixed) {
    return showTitle ? fixed - HEADER_HEIGHT : fixed
  }
  return showTitle ? `calc(100% - ${HEADER_HEIGHT}px)` : '100%'
}

const PrintSection: React.FC<PrintSectionProps> = ({
  section,
  sectionName,
  fixedHeight,
  sectionWidthPx,
  printableWidthPx
}) => {
  if (!section) {
    return (
      <ErrorPlaceholder>
        <WarningOutlined style={{ fontSize: 18 }} />
        <span>{sectionName ?? 'Section'}: failed to load</span>
      </ErrorPlaceholder>
    )
  }
  const useFixedHeight = fixedHeight && !GROWABLE_TYPES.has(section.type)
  const isTable = section.type === 'table'

  if (isTable) {
    const tableSection = section as CustomReportTableSection
    return (
      <>
        <SectionTitle style={{ height: HEADER_HEIGHT }}>{getTitleByLanguage(section)}</SectionTitle>
        {tableSection.style?.switchRowsAndColumns ? (
          <PrintTableSectionTransposed section={tableSection} />
        ) : (
          <PrintTableSection section={tableSection} sectionWidth={sectionWidthPx ?? printableWidthPx} />
        )}
      </>
    )
  }

  const isCard = section.type === 'infobox' || section.type === 'graph' || section.type === 'pie'
  const showTitle = section.type !== 'infobox'

  return (
    <>
      {showTitle && <SectionTitle style={{ height: HEADER_HEIGHT }}>{getTitleByLanguage(section)}</SectionTitle>}
      {isCard ? (
        <CardFrame style={{ height: cardHeight(useFixedHeight ? fixedHeight : undefined, showTitle) }}>
          <SectionContent section={section} printMode />
        </CardFrame>
      ) : (
        <SectionContent section={section} printMode />
      )}
    </>
  )
}

export default React.memo(PrintSection)
